import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, Transition as _Transition } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_2 = { class: "mt-2 relative" }
const _hoisted_3 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_4 = { class: "relative" }
const _hoisted_5 = { class: "flex group" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" }
const _hoisted_8 = ["disabled", "onClick", "data-focus", "data-hover"]
const _hoisted_9 = {
  key: 2,
  class: "mt-2 text-sm text-red-600"
}

import { 
		clsx
	} from 'clsx'
	
	import {
		ref,
		computed,
		nextTick,
		PropType
	} from 'vue'
	
	import {
		Combobox,
		ComboboxButton,
		ComboboxInput,
		ComboboxLabel,
		ComboboxOption,
		ComboboxOptions,
		Listbox,
		ListboxButton,
		ListboxLabel,
		ListboxOption,
		ListboxOptions,
		TransitionRoot
	} from '@headlessui/vue'

	import {
		CheckIcon,
		ChevronUpDownIcon
	} from '@heroicons/vue/24/solid'
	// #endregion


	// #region setup
	
export default /*#__PURE__*/_defineComponent({
  __name: 'TwSelect',
  props: {
		modelValue: {
			type: [String, Number, Array],
			default: null
		},
		clearable: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		filterable: {
			type: Boolean,
			default: false
		},
		filterPlaceholder: {
			type: String,
			default: "Type to filter list..."
		},
		hideLabel: {
			type: Boolean,
			default: false
		},
		label: {
			type: String,
			default: ''
		},
		multiple: {
			type: Boolean,
			default: false
		},
		noResultsPlaceholder: {
			type: String,
			default: "Nothing found."
		},
		options: {
			type: Array as PropType<Array<{label:string,value:string}>>,
			default: () => []
		},
		placeholder: {
			type: String,
			default: "None selected"
		},
		required: {
			type: Boolean,
			default: false
		},
		requiredError: {
			type: String,
			default: "This field is required."
		},
		searchable: {
			type: Boolean,
			default: false
		},
		searchMethod: {
			type: Function,
			default: () => {return}
		},
		searchPlaceholder: {
			type: String,
			default: "Type to search..."
		},
		valid: {
			type: Boolean,
			default: true
		},
		validationError: {
			type: String,
			default: "This field is not valid."
		}
	},
  emits: [
		'update:modelValue'
	],
  setup(__props, { expose, emit }) {

const props = __props;

// #region imports

	

	
	// #endregion


	// #region editing model value
	/**
	 * Text to display in the select box. Formats multiple selections as a comma separated string.
	 * @yields {string}
	 */
	const modelLabel = computed(() =>{
		if(Array.isArray(props.modelValue)){
			let selectedOptions: any = []
			props.modelValue.forEach(value =>{
				const selectedOption: any = props.options.find((option: any) => {
					return option.value === value
				})
				selectedOptions.push(selectedOption?.label)
			})
			return selectedOptions.join(', ')
		}
		else{
			const selectedOption: any = props.options.find((option: any) => {
				return option.value === props.modelValue
			})
			return selectedOption?.label || null
		}
	})

	/**
	 * Updates the bound value of the select menu
	 * @function onSelect
	 * @param {Object} option - the selected option of the select menu
	 */
	async function onSelect(value: any) {
		emit('update:modelValue', value)
		await nextTick()
		validate()
		return
	}

	/**
	 * Clears the selected value
	 * @function clear
	 */
	async function clear() {
		if(Array.isArray(props.modelValue)) {
			emit('update:modelValue', [])
		}
		else {
			emit('update:modelValue', '')
		}
		await nextTick()
		return
	}
	// #endregion

	// #region validation
	const _valid = ref(true)
	function validate() {
		if(props.multiple && !props.modelValue?.length ) {
			_valid.value = false
			return _valid.value
		}
		else if((props.required && !props.modelValue) || props.valid === false){
			_valid.value = false
			return _valid.value
		}
		else if(props.required && props.modelValue){
			_valid.value = true
			return _valid.value
		}
	}
	// #endregion


	// #region filtering
	const query = ref('')

	function updateQuery(e) {
		query.value = e.target.value
		if(props.searchable) {
			props.searchMethod(query.value)
		}
	}

	const _placeholder = ref(props.placeholder)

	/**
     * Updates the placeholder text when filtering or searching and the combobox is focused
     * @function setPlaceholder
     */
	function setPlaceholder() {
		if(props.filterable) _placeholder.value = props.filterPlaceholder
		else if(props.searchable) _placeholder.value = props.searchPlaceholder
	}

	/**
     * Clears the placeholder text when focus is lost on the combobox
     * @function setPlaceholder
     */
	function clearPlaceholder() {
		_placeholder.value = props.placeholder
	}

	/**
	 * List of options filtered by filter text
	 * @yields {array}
	 */
	const filteredOptions = computed(() => {
		return query.value === '' ? props.options : props.options.filter((option: any) => {
			return !props.filterable || !query.value || option.label.toLowerCase().replace(/\s+/g, '').includes(query.value.toLowerCase().replace(/\s+/g, '')) == true
		})
	})

	/**
	 * If no options remain when filtered by filter text
	 * @yields {boolean}
	 */
	const noFilteredOptions = computed(() => {
		return filteredOptions.value.length === 0 && query.value !== ''
	})
	// #endregion

	expose({
		validate
	})

	const classes = clsx([
		// Basic layout
		'relative block w-full',
		// Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
		'before:absolute before:inset-px  before:bg-white before:shadow',
		// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
		'dark:before:hidden',
		// Focus ring
		'after:pointer-events-none after:absolute after:inset-0  after:ring-inset after:ring-transparent sm:after:focus-within:ring-2 sm:after:focus-within:ring-blue-500',
		// Disabled state
		'has-[[data-disabled]]:opacity-50 before:has-[[data-disabled]]:bg-zinc-950/5 before:has-[[data-disabled]]:shadow-none',
		// Invalid state
		'before:has-[[data-invalid]]:shadow-red-500/10',
		props.clearable ? 'before:rounded-l-[calc(theme(borderRadius.lg)-1px)] after:rounded-l-lg' : 'before:rounded-[calc(theme(borderRadius.lg)-1px)] after:rounded-lg'
	])

	const selectClasses = clsx([
		// Basic layout
		'relative block w-full appearance-none  px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
		// Typography
		'text-base/6 sm:text-sm/6',
		// Border
		'border border-zinc-950/10 data-[hover]:border-zinc-950/20 dark:border-white/10 dark:data-[hover]:border-white/20',
		// Background color
		'bg-transparent dark:bg-white/5',
		// Hide default focus styles
		'focus:outline-none',
		// Invalid state
		'data-[invalid]:border-red-500 data-[invalid]:data-[hover]:border-red-500 data-[invalid]:dark:border-red-500 data-[invalid]:data-[hover]:dark:border-red-500',
		// Disabled state
		'data-[disabled]:border-zinc-950/20 dark:data-[hover]:data-[disabled]:border-white/15 data-[disabled]:dark:border-white/15 data-[disabled]:dark:bg-white/[2.5%]',
		props.clearable ? 'rounded-l-lg' : 'rounded-lg'
	])

	const buttonClasses = clsx([
		// Base
		'relative isolate inline-flex items-center justify-center gap-x-2 rounded-r-lg border text-base/6 font-semibold',
		// Focus
		'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500',
		// Disabled
		'data-[disabled]:opacity-50',
		// Icon
		'[&>[data-slot=icon]]:-mx-0.5 [&>[data-slot=icon]]:my-0.5 [&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-[--btn-icon] [&>[data-slot=icon]]:sm:my-1 [&>[data-slot=icon]]:sm:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-[hover]:[--btn-icon:ButtonText]',
		// Optical border, implemented as the button background to avoid corner artifacts
		'border-transparent bg-[--btn-border]',
		// Dark mode: border is rendered on `after` so background is set to button background
		'dark:bg-[--btn-bg]',
		// Button background, implemented as foreground layer to stack on top of pseudo-border layer
		'before:absolute before:inset-0 before:-z-10 before:rounded-r-[calc(theme(borderRadius.lg)-1px)] before:bg-[--btn-bg]',
		// Drop shadow, applied to the inset `before` layer so it blends with the border
		'before:shadow',
		// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
		'dark:before:hidden',
		// Dark mode: Subtle white outline is applied using a border
		'dark:border-white/5',
		// Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
		'after:absolute after:inset-0 after:-z-10 after:rounded-r-[calc(theme(borderRadius.lg)-1px)]',
		// Inner highlight shadow
		'after:shadow-[shadow:inset_0_1px_theme(colors.white/15%)]',
		// White overlay on hover
		'after:data-[active]:bg-[--btn-hover-overlay] after:data-[hover]:bg-[--btn-hover-overlay]',
		// Dark mode: `after` layer expands to cover entire button
		'dark:after:-inset-px dark:after:rounded-r-lg',
		// Disabled
		'before:data-[disabled]:shadow-none after:data-[disabled]:shadow-none',
		'text-zinc-950 [--btn-bg:white] [--btn-border:theme(colors.zinc.950/10%)] [--btn-hover-overlay:theme(colors.zinc.950/2.5%)] data-[active]:[--btn-border:theme(colors.zinc.950/15%)] data-[hover]:[--btn-border:theme(colors.zinc.950/15%)]',
		'dark:[--btn-hover-overlay:theme(colors.zinc.950/5%)]',
		'[--btn-icon:theme(colors.zinc.400)] data-[active]:[--btn-icon:theme(colors.zinc.500)] data-[hover]:[--btn-icon:theme(colors.zinc.500)]',
		'px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6',
	])

	const focused = ref(false)
	const hovered = ref(false)


return (_ctx: any,_cache: any) => {
  const _component_XMarkIcon = _resolveComponent("XMarkIcon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (__props.searchable || __props.filterable)
      ? (_openBlock(), _createBlock(_unref(Combobox), {
          key: 0,
          as: "div",
          "model-value": __props.modelValue,
          multiple: __props.multiple,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = value => onSelect(value))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ComboboxLabel), { class: "block text-sm font-medium text-gray-700" }, {
              default: _withCtx(() => [
                (__props.required)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_1, " * "))
                  : _createCommentVNode("", true),
                _createElementVNode("span", {
                  class: _normalizeClass({ 'sr-only': __props.hideLabel})
                }, _toDisplayString(__props.label), 3)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(ComboboxInput), {
                id: "comboBoxInput",
                class: _normalizeClass(["relative w-full rounded-md border-0 ring-offset-1 ring-1 ring-gray-300 py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 sm:text-sm", {
						'bg-gray-200 cursor-not-allowed pointer-events-none': __props.disabled, 
						'bg-white cursor-default': !__props.disabled,
						'border-0 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500': !_valid.value
					}]),
                onFocus: setPlaceholder,
                onBlur: clearPlaceholder,
                autocomplete: "none",
                onChange: _cache[0] || (_cache[0] = ($event: any) => (updateQuery($event))),
                "display-value": (v) => Array.isArray(v) ? v.join(', ') : String(v),
                placeholder: _placeholder.value
              }, null, 8, ["class", "display-value", "placeholder"]),
              _createVNode(_unref(ComboboxButton), { class: "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" }, {
                default: _withCtx(() => [
                  _withDirectives(_createVNode(_unref(ChevronUpDownIcon), {
                    class: "h-5 w-5 text-gray-400",
                    "aria-hidden": "true"
                  }, null, 512), [
                    [_vShow, !__props.disabled]
                  ])
                ]),
                _: 1
              }),
              _createVNode(_unref(TransitionRoot), {
                leave: "transition ease-in duration-100",
                "leave-from": "opacity-100",
                "leave-to": "opacity-0",
                onAfterLeave: _cache[1] || (_cache[1] = ($event: any) => (query.value = ''))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ComboboxOptions), { class: "absolute z-30 mt-2 w-full bg-white shadow-lg max-h-60 rounded-sm py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" }, {
                    default: _withCtx(() => [
                      _withDirectives(_createElementVNode("div", {
                        as: "template",
                        class: "relative cursor-default select-none py-2 px-4 text-gray-700"
                      }, _toDisplayString(__props.noResultsPlaceholder), 513), [
                        [_vShow, _unref(noFilteredOptions)]
                      ]),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((__props.filterable ? _unref(filteredOptions) : __props.options), (option) => {
                        return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                          as: "template",
                          key: option.value,
                          value: option.value
                        }, {
                          default: _withCtx(({ active, selected }) => [
                            _createElementVNode("li", {
                              class: _normalizeClass([active ? 'text-white bg-yellow-500' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9'])
                            }, [
                              _createElementVNode("span", {
                                class: _normalizeClass([selected ? 'font-semibold' : 'font-normal', 'block truncate'])
                              }, _toDisplayString(option.label), 3),
                              selected
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 0,
                                    class: _normalizeClass([active ? 'text-white' : 'text-yellow-500', 'absolute inset-y-0 right-0 flex items-center pr-4'])
                                  }, [
                                    _createVNode(_unref(CheckIcon), {
                                      class: "h-5 w-5",
                                      "aria-hidden": "true"
                                    })
                                  ], 2))
                                : _createCommentVNode("", true)
                            ], 2)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }, 8, ["model-value", "multiple"]))
      : (_openBlock(), _createBlock(_unref(Listbox), {
          key: 1,
          as: "div",
          "model-value": __props.modelValue,
          multiple: __props.multiple,
          "onUpdate:modelValue": _cache[7] || (_cache[7] = value => onSelect(value))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ListboxLabel), { class: "block text-sm font-medium text-gray-700" }, {
              default: _withCtx(() => [
                (__props.required && !__props.hideLabel)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, " * "))
                  : _createCommentVNode("", true),
                _createElementVNode("span", {
                  class: _normalizeClass([{ 'sr-only': __props.hideLabel}, 'mb-2'])
                }, _toDisplayString(__props.label), 3)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_unref(ListboxButton), {
                  id: "listBoxButton",
                  class: _normalizeClass([{
							'bg-gray-200 cursor-not-allowed pointer-events-none': __props.disabled, 
							'bg-white cursor-default': !__props.disabled,
							'rounded-r-md': !__props.clearable,
							'border-0 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500': !_valid.value
						}, _unref(classes), 'z-10 min-w-0'])
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", {
                      class: _normalizeClass(["block truncate text-left", [{'text-gray-500': !_unref(modelLabel)}, _unref(selectClasses)]])
                    }, [
                      _createTextVNode(_toDisplayString(_unref(modelLabel) || _placeholder.value) + " ", 1),
                      (__props.required && !_unref(modelLabel))
                        ? (_openBlock(), _createElementBlock("span", _hoisted_6, "*"))
                        : _createCommentVNode("", true)
                    ], 2),
                    _createElementVNode("span", _hoisted_7, [
                      _withDirectives(_createVNode(_unref(ChevronUpDownIcon), {
                        class: "h-5 w-5 text-gray-400",
                        "aria-hidden": "true"
                      }, null, 512), [
                        [_vShow, !__props.disabled]
                      ])
                    ])
                  ]),
                  _: 1
                }, 8, ["class"]),
                (__props.clearable)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      "aria-label": "Clear selected value",
                      disabled: __props.disabled || !_unref(modelLabel),
                      type: "button",
                      onClick: _withModifiers(clear, ["stop"]),
                      onFocusin: _cache[3] || (_cache[3] = ($event: any) => (focused.value = true)),
                      onFocusout: _cache[4] || (_cache[4] = ($event: any) => (focused.value = false)),
                      "data-focus": focused.value ? true : null,
                      onMouseover: _cache[5] || (_cache[5] = ($event: any) => (hovered.value = true)),
                      onMouseout: _cache[6] || (_cache[6] = ($event: any) => (hovered.value = false)),
                      "data-hover": hovered.value ? true : null,
                      class: _normalizeClass([_unref(buttonClasses), '-ml-[1px] focus:z-20'])
                    }, [
                      _createVNode(_component_XMarkIcon, {
                        class: "h-5 w-5",
                        "aria-hidden": "true"
                      })
                    ], 42, _hoisted_8))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_Transition, {
                "leave-active-class": "transition ease-in duration-100",
                "leave-from-class": "opacity-100",
                "leave-to-class": "opacity-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ListboxOptions), { class: "absolute z-30 mt-1 w-full bg-white shadow-lg max-h-60 rounded-sm py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option) => {
                        return (_openBlock(), _createBlock(_unref(ListboxOption), {
                          as: "template",
                          key: option.value,
                          value: option.value
                        }, {
                          default: _withCtx(({ active, selected }) => [
                            _createElementVNode("li", {
                              class: _normalizeClass([active ? 'text-gray-900 bg-yellow-500' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9 focus:outline-none'])
                            }, [
                              _createElementVNode("span", {
                                class: _normalizeClass([selected ? 'font-semibold' : 'font-normal', 'block truncate'])
                              }, _toDisplayString(option.label), 3),
                              selected
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 0,
                                    class: _normalizeClass([active ? 'text-yellow-500' : 'text-gray-900', 'absolute inset-y-0 right-0 flex items-center pr-4 focus:outline-none'])
                                  }, [
                                    _createVNode(_unref(CheckIcon), {
                                      class: "h-5 w-5 text-gray-900",
                                      "aria-hidden": "true"
                                    })
                                  ], 2))
                                : _createCommentVNode("", true)
                            ], 2)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }, 8, ["model-value", "multiple"])),
    (!_valid.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(props.required && !props.modelValue ? __props.requiredError : __props.validationError), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})